import React, { useState, useRef } from "react";
import { App } from "antd";
import {
  UploadOutlined, FilePdfOutlined, FontSizeOutlined,
  DeleteOutlined, CopyOutlined, CheckCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import "../PdfTools.css";
import "./OcrPdf.css";

const OcrPdf = () => {
  const { message } = App.useApp();
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver,  setDragOver]  = useState(false);
  const [extracted, setExtracted] = useState(""); // extracted text
  const [copied,    setCopied]    = useState(false);
  const fileRef = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  const pickFile = (incoming) => {
    const f = Array.from(incoming).find((f) =>
      f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (!f) { message.error("Please select a PDF file."); return; }
    setFile(f);
    setExtracted("");
  };

  const reset = () => { setFile(null); setExtracted(""); };

  const handleOcr = async () => {
    if (!file) return;
    setUploading(true);
    setExtracted("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("Source", isModule ? "Private-Web" : "Public-Web");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/extract-text`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (!res.data.error && res.data.data) {
        const text = res.data.data.text || res.data.data.extractedText || JSON.stringify(res.data.data);
        setExtracted(text);
        message.success("Text extracted successfully!");
      } else {
        message.error(res.data.message || "OCR failed.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(extracted).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`pt-root ${isModule ? "pt-module" : ""}`}>
        <div className="pt-inner">

          <div className="pt-header">
            <div className="pt-header-icon" style={{ background: "#faf5ff" }}>
              <FontSizeOutlined style={{ color: "#7b3fe4" }} />
            </div>
            <div>
              <h1 className="pt-title">OCR PDF</h1>
              <p className="pt-subtitle">Extract all text content from any PDF file — including scanned documents.</p>
            </div>
          </div>

          {!file && !extracted && (
            <div
              className={`pt-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
                onChange={(e) => { pickFile(e.target.files); e.target.value = ""; }} />
              <FilePdfOutlined className="pt-dropzone-icon" />
              <div className="pt-dropzone-text">Click to select or drag your PDF here</div>
              <div className="pt-dropzone-hint">Only PDF files supported</div>
              <button className="pt-choose-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                <UploadOutlined /> Choose File
              </button>
            </div>
          )}

          {file && !extracted && (
            <>
              <div className="pt-file-row">
                <FilePdfOutlined className="pt-file-icon" />
                <span className="pt-file-name">{file.name}</span>
                <span className="pt-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                <button className="pt-file-remove" onClick={reset}><DeleteOutlined /></button>
              </div>
              <button className="pt-action-btn" onClick={handleOcr} disabled={uploading}>
                {uploading ? <span className="pt-spinner" /> : <FontSizeOutlined />}
                {uploading ? "Extracting text…" : "Extract Text"}
              </button>
            </>
          )}

          {extracted && (
            <div className="ocr-result">
              <div className="ocr-result-header">
                <div className="ocr-result-title">
                  <CheckCircleFilled style={{ color: "#10b981" }} /> Extracted Text
                </div>
                <button className="ocr-copy-btn" onClick={handleCopy}>
                  <CopyOutlined /> {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <textarea className="ocr-text-area" readOnly value={extracted} />
              <div className="ocr-word-count">
                ~{extracted.trim().split(/\s+/).length.toLocaleString()} words · {extracted.length.toLocaleString()} characters
              </div>
              <div className="pt-success-actions" style={{ justifyContent: "flex-start" }}>
                <button className="pt-restart-btn" onClick={reset}>Extract from another PDF</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default OcrPdf;
